<template>
    <div
        ref="container"
        class="subtitle-modal"
        @focusin="onFocusIn"
        @focusout="onFocusOut"
    >
        <div class="subtitle-modal__title">{{ modalTitle }}</div>
        <div v-if="isSubtitles" class="subtitle-modal__wrapper">
            <div class="subtitle-modal__subtitle">{{ modalSubtitle }}</div>
            <button
                v-for="(subtitle,index) in allTextTracks" 
                :id="getId(index,ButtonsTypes.SUBTITLE)" 
                :key="subtitle.id" 
                :data-keyup="getUpButton(ButtonsTypes.SUBTITLE, index)"
                :data-keydown="getDownButton(ButtonsTypes.SUBTITLE, index)"
                class="subtitle-modal__option"
                :class="{'subtitle-modal__option_active':isActive(subtitle.id)}"
                @focus="onFocus(subtitle.id)"
                @click="onClick(subtitle.id, ButtonsTypes.SUBTITLE)"
            >
                <span
                    class="subtitle-modal__radio"
                    :class="{'subtitle-modal__radio_active':isChecked(subtitle.id)}" 
                />
                <span class="subtitle-modal__text">{{ titleTextTrack(subtitle) }}</span>
            </button>
            <button 
                id="off" 
                class="subtitle-modal__option" 
                :class="{'subtitle-modal__option_active':isActive(ButtonsTypes.OFF)}" 
                :data-keydown="getDownButton(ButtonsTypes.OFF)"
                :data-keyup="getUpButton(ButtonsTypes.OFF)"
                @click="onClick(ButtonsTypes.OFF, ButtonsTypes.SUBTITLE)"
                @focus="onFocus(ButtonsTypes.OFF)">
                <span
                    class="subtitle-modal__radio" 
                    :class="{'subtitle-modal__radio_active':isChecked(ButtonsTypes.OFF)}" 
                />
                <span class="subtitle-modal__text">{{ modalOff }}</span>
            </button>
        </div>
        <div v-if="isShowAudio" class="subtitle-modal__wrapper">
            <div class="subtitle-modal__subtitle">{{ modalAudio }}</div>
            <button
                v-for="(audio, index) in allAudioTracks" 
                :id="getId(index, ButtonsTypes.AUDIO)" 
                :key="audio.id" 
                :data-keyup="getUpButton(ButtonsTypes.AUDIO, index)"
                :data-keydown="getDownButton(ButtonsTypes.AUDIO, index)"
                class="subtitle-modal__option"
                :class="{'subtitle-modal__option_active':isActive(audio.id)}"
                @focus="onFocus(audio.id)"
                @click="onClick(audio.id,  ButtonsTypes.AUDIO)"
            >
                <span
                    class="subtitle-modal__radio"
                    :class="{'subtitle-modal__radio_active':isChecked(audio.id)}" 
                />
                <span class="subtitle-modal__text">{{ titleAudioTrack(audio, index) }}</span>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { type PropType, type Ref, ref, computed, watch, nextTick } from "vue";
    import { Strings } from "@/locales/types/enums";
    import { type PlayerTracks } from '@ca.vmedia.tv/domains';
    import { useI18n } from "vue-i18n";
    import { usePlayerStore } from "@/stores/Player";
    import { storeToRefs } from 'pinia'
    import { ButtonsTypes } from "./types/enums/ButtonsTypes";

    const PlayerStoreModule = usePlayerStore();
    const {
        saveSubtitles,
        saveAudio,
    } = PlayerStoreModule;
    const {
        subtitles: checkedSubtitles,
        audio: checkedAudio,
    } = storeToRefs(PlayerStoreModule);

    const props = defineProps({
        showModal: {
            type: Boolean,
            default: false,
        },
        allSubtitles: {
            type: Array as PropType<PlayerTracks[] | null>,
            default: () => [],
            required: false,
        },
        allAudio: {
            type: Array as PropType<PlayerTracks[] | null>,
            default: () => [],
            required: false,
        },
        availableAudio: {
            type: Boolean,
            required: true,
        },
    });

    const emit = defineEmits([
        'on-modal-click'
    ]);

    const { $navigator } = useNuxtApp();
    const { t } = useI18n();
    const modalTitle: string = t(Strings.SUBTITLES_MODAL_TITLE).toString();
    const modalSubtitle: string = t(Strings.SUBTITLES_MODAL_SUBTITLE).toString();
    const modalOff: string = t(Strings.SUBTITLES_MODAL_OFF).toString();
    const modalAudio: string = t(Strings.SUBTITLES_MODAL_AUDIO).toString();
    const subtitleCaption: string = t(Strings.SUBTITLES_MODAL_CLOSED_CAPTION).toString();
    const focusedItemId: Ref<string> = ref("");
    const container: Ref<HTMLElement | null> = ref(null);

    const isSubtitles = computed((): boolean => allTextTracks.value?.length > 0);
    const originalAudioTracks = computed((): PlayerTracks[] => props.allAudio?.filter((audio) => !audio.lang?.endsWith("m") && !audio.language?.endsWith("m")) ?? []);
    const descriptiveAudioTrack = computed((): PlayerTracks | undefined => props.allAudio?.find((audio) => audio.lang?.endsWith("m") || audio.language?.endsWith("m")));

    // sort audio tracks to move descriptive audio to the end of the list
    const allAudioTracks = computed((): PlayerTracks[] => descriptiveAudioTrack.value ? [...originalAudioTracks.value, descriptiveAudioTrack.value] : originalAudioTracks.value);
 
    const isShowAudio = computed((): boolean => props.availableAudio && allAudioTracks.value?.length > 1);
    const allTextTracks = computed((): PlayerTracks[] => props.allSubtitles?.map(({ label, lang, language, id, kind }) => ({
        label: kind === "captions" || kind === "caption" ? subtitleCaption : label ?? modalSubtitle,
        lang,
        language,
        id,
        kind,
    })) ?? []);


    const getId = (index: number, type: string): string => {
        if (type === ButtonsTypes.SUBTITLE) {
            return `${String(allTextTracks.value[index].id)}-${type}`;
        }
        return `${allAudioTracks.value?.[index].id}-${type}`;
    };
    const onFocusIn = () => {
        $navigator.on("keypress:back", closeModal);
    };
    const onFocusOut = () => {
        setTimeout(() => {
            const focusedElement = document.activeElement;
            if (focusedElement && !container.value?.contains(focusedElement)) {
                closeModal();
            }
        }, 300);

        $navigator.off("keypress:back", closeModal);
    };
    const onFocus = (id: string | number) => {
        focusedItemId.value = String(id);
    };
    const isActive = (id: string | number): boolean => {
        if (focusedItemId.value === String(id)) {
            return true;
        }
        return false;
    };
    const isChecked = (id: string | number): boolean => {
        if (checkedSubtitles.value === String(id) || checkedAudio.value === String(id)) {
            return true;
        }
        return false;
    };
    const onClick = (id: string | number, type: string) => {
        if (type === ButtonsTypes.SUBTITLE) {
            saveSubtitles(String(id));
        } else {
            saveAudio(String(id));
        }
        setTimeout(() => {
            closeModal();
        }, 500);
    };
    const closeModal = () => {
        emit("on-modal-click");
    };
    const getDownButton = (type: ButtonsTypes, index?: number): string => {
        if (type === ButtonsTypes.SUBTITLE && index !== undefined) {
            const nextInd = index + 1;
            if (nextInd < allTextTracks.value.length) {
                return getId(nextInd, ButtonsTypes.SUBTITLE);
            }
            return ButtonsTypes.OFF;
        } 
        if (type === ButtonsTypes.AUDIO && index !== undefined) {
            const nextInd = index + 1;
            if (nextInd < allAudioTracks.value?.length) {
                return getId(nextInd, ButtonsTypes.AUDIO);
            } else if (allTextTracks.value.length > 0) { // if we have more than one subtitle track, we should return id of the first subtitle track
                return getId(0, ButtonsTypes.SUBTITLE);
            }
            // if we haven`t subtitle tracks, we should return id of the first audio track
            return getId(0, ButtonsTypes.AUDIO);
        }
        // case for the subtitles 'off' button when we show audio tracks
        if (type === ButtonsTypes.OFF && isShowAudio.value) {
            return getId(0, ButtonsTypes.AUDIO);
        }
        // case for the subtitles 'off' button when we don't show audio tracks
        return getId(0, ButtonsTypes.SUBTITLE);
    };
    const getUpButton = (type: ButtonsTypes, index?: number): string => {
        if (type === ButtonsTypes.SUBTITLE && index !== undefined) {
            const nextInd = index - 1;
            if (nextInd >= 0) {
                return getId(nextInd, ButtonsTypes.SUBTITLE);
            } else if (isShowAudio.value) { // if we show audio tracks, we should return id of the last audio track
                return getId(allAudioTracks.value?.length - 1, ButtonsTypes.AUDIO);
            }
            return ButtonsTypes.OFF;
        } 
        if (type === ButtonsTypes.AUDIO && index !== undefined) {
            const nextInd = index - 1;
            if (nextInd >= 0) {
                return getId(nextInd, ButtonsTypes.AUDIO);
            } else if (allTextTracks.value.length > 0) { // if we have text tracks, we should return id of the subtitles 'off' button
                return ButtonsTypes.OFF;
            }
            return getId(index, ButtonsTypes.AUDIO);
        }
        // case for the subtitles 'off' button
        return getId(allTextTracks.value.length - 1, ButtonsTypes.SUBTITLE);
    };
    const titleAudioTrack = (track: PlayerTracks, index: number): string => {
        // if lang or language ends on 'm' we should return 'descriptive audio'
        if (track.lang?.endsWith("m") || track.language?.endsWith("m")) {
            return t(Strings.SUBTITLES_MODAL_DESCRIPTIVE_AUDIO).toString();
        }
        // if we have more than 1 original audio track we should return 'original audio' with index
        return `${t(Strings.SUBTITLES_MODAL_ORIGINAL_AUDIO).toString()} ${originalAudioTracks.value.length > 1 ? index + 1 : ""}`;
    };
    const titleTextTrack = (track: PlayerTracks): string => {
        // if we have more than 1 subtitle track with the same 'label' we should return 'label' with index
        const dublicatedLabelTracks = allTextTracks.value.filter((subtitle) => subtitle.label === track.label);
        if (dublicatedLabelTracks.length > 1) {
            return `${track.label} ${dublicatedLabelTracks.indexOf(track) + 1}`;
        }
        return track.label;
    };


    watch( 
        allTextTracks,
        async () => {
            if (isSubtitles.value) {
                void nextTick(() => {
                    const id = `${String(allTextTracks.value[0]?.id)}-${ButtonsTypes.SUBTITLE}`;
                    document.getElementById(id)?.focus();
                });
            }
        },
        { immediate: true }
    ); 
    watch(
        allAudioTracks,
        async () => {
            if (allAudioTracks.value?.length > 0 && !isSubtitles.value) {
                void nextTick(() => {
                    const id = `${allAudioTracks.value?.[0]?.id}-${ButtonsTypes.AUDIO}`;
                    document.getElementById(String(id))?.focus();
                });
            }
        },
        { immediate: true }
    );
</script>

<style lang="scss">
    @import url("styles.scss");
</style>
